import React, { useState, useRef, useEffect, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUpDownLeftRight } from "@fortawesome/free-solid-svg-icons";

const getButtonStyle = (data) => {
  const x = data.button_x !== undefined && data.button_x !== null ? data.button_x : 50;
  const y = data.button_y !== undefined && data.button_y !== null ? data.button_y : 85;
  return {
    left: `${x}%`,
    top: `${y}%`,
    transform: "translate(-50%, -50%)",
  };
};

const getTextStyle = (data) => {
  const x = data.text_x !== undefined && data.text_x !== null ? data.text_x : 50;
  const y = data.text_y !== undefined && data.text_y !== null ? data.text_y : 35;
  return {
    left: `${x}%`,
    top: `${y}%`,
    transform: "translate(-50%, -50%)",
  };
};

const BannerPreview = ({ data, onPositionChange }) => {
  const isEmpty = !data.title && !data.text && !data.image && !data.button_text;

  const containerRef = useRef(null);
  const rafRef = useRef(null);
  const [draggingItem, setDraggingItem] = useState(null); // 'button' | 'text' | null
  const [buttonCoords, setButtonCoords] = useState({ x: 50, y: 85 });
  const [textCoords, setTextCoords] = useState({ x: 50, y: 35 });

  useEffect(() => {
    if (draggingItem !== "button") {
      const x = data.button_x !== undefined && data.button_x !== null ? Number(data.button_x) : 50;
      const y = data.button_y !== undefined && data.button_y !== null ? Number(data.button_y) : 85;
      setButtonCoords({ x, y });
    }
  }, [data.button_x, data.button_y, draggingItem]);

  useEffect(() => {
    if (draggingItem !== "text") {
      const x = data.text_x !== undefined && data.text_x !== null ? Number(data.text_x) : 50;
      const y = data.text_y !== undefined && data.text_y !== null ? Number(data.text_y) : 35;
      setTextCoords({ x, y });
    }
  }, [data.text_x, data.text_y, draggingItem]);

  const calculateCoords = useCallback((clientX, clientY) => {
    if (!containerRef.current) return null;
    const rect = containerRef.current.getBoundingClientRect();
    const rawX = ((clientX - rect.left) / rect.width) * 100;
    const rawY = ((clientY - rect.top) / rect.height) * 100;

    const x = Math.max(5, Math.min(95, Math.round(rawX * 10) / 10));
    const y = Math.max(5, Math.min(95, Math.round(rawY * 10) / 10));

    return { x, y };
  }, []);

  const handleStartDrag = (e, itemType) => {
    if (!onPositionChange) return;
    e.preventDefault();
    e.stopPropagation();

    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;

    const calc = calculateCoords(clientX, clientY);
    if (calc) {
      setDraggingItem(itemType);
      if (itemType === "button") setButtonCoords({ x: calc.x, y: calc.y });
      if (itemType === "text") setTextCoords({ x: calc.x, y: calc.y });
    }
  };

  const handleMoveDrag = useCallback(
    (e) => {
      if (!draggingItem || !containerRef.current) return;
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;

      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }

      rafRef.current = requestAnimationFrame(() => {
        const calc = calculateCoords(clientX, clientY);
        if (calc) {
          if (draggingItem === "button") {
            setButtonCoords({ x: calc.x, y: calc.y });
            if (onPositionChange) {
              onPositionChange({ button_x: calc.x, button_y: calc.y });
            }
          } else if (draggingItem === "text") {
            setTextCoords({ x: calc.x, y: calc.y });
            if (onPositionChange) {
              onPositionChange({ text_x: calc.x, text_y: calc.y });
            }
          }
        }
      });
    },
    [draggingItem, calculateCoords, onPositionChange]
  );

  const handleEndDrag = useCallback(() => {
    if (!draggingItem) return;
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    setDraggingItem(null);
  }, [draggingItem]);

  useEffect(() => {
    if (draggingItem) {
      window.addEventListener("mousemove", handleMoveDrag);
      window.addEventListener("mouseup", handleEndDrag);
      window.addEventListener("touchmove", handleMoveDrag);
      window.addEventListener("touchend", handleEndDrag);
    }
    return () => {
      window.removeEventListener("mousemove", handleMoveDrag);
      window.removeEventListener("mouseup", handleEndDrag);
      window.removeEventListener("touchmove", handleMoveDrag);
      window.removeEventListener("touchend", handleEndDrag);
    };
  }, [draggingItem, handleMoveDrag, handleEndDrag]);

  const buttonStyle = draggingItem === "button"
    ? { left: `${buttonCoords.x}%`, top: `${buttonCoords.y}%`, transform: "translate(-50%, -50%)" }
    : getButtonStyle(data);

  const textStyle = draggingItem === "text"
    ? { left: `${textCoords.x}%`, top: `${textCoords.y}%`, transform: "translate(-50%, -50%)" }
    : getTextStyle(data);

  return (
    <div
      ref={containerRef}
      className="relative w-full max-w-xl mx-auto aspect-[500/200] rounded-xl overflow-hidden group shadow-inner select-none bg-gray-100"
      style={{ backgroundColor: data.background_color }}
    >
      {/* BACKGROUND IMAGE */}
      {data.image ? (
        <>
          <img
            src={data.image}
            className="w-full h-full object-cover select-none pointer-events-none"
            alt="Banner Background"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-black/10 pointer-events-none" />
        </>
      ) : (
        <div className="w-full h-full flex items-center justify-center">
          {isEmpty && (
            <div className="text-gray-300 flex flex-col items-center gap-1 sm:gap-2">
              <div className="w-8 h-8 sm:w-16 sm:h-16 rounded-full border-2 sm:border-4 border-dashed border-gray-200 flex items-center justify-center">
                <span className="text-sm sm:text-2xl font-bold">?</span>
              </div>
              <p className="text-xs sm:text-sm font-medium">Design your banner</p>
            </div>
          )}
        </div>
      )}

      {/* CONTENT CONTAINER */}
      <div className="absolute inset-0 p-2.5 sm:p-4 md:p-6 flex flex-col pointer-events-none z-10">
        {/* TEXT CONTENT (DRAGGABLE) */}
        {data.text && (
          <div
            onMouseDown={(e) => handleStartDrag(e, "text")}
            onTouchStart={(e) => handleStartDrag(e, "text")}
            style={{ ...textStyle, color: data.text_color }}
            className={`absolute pointer-events-auto w-max max-w-[85%] ${
              draggingItem === "text" ? "z-30 cursor-grabbing scale-105 transition-none" : "transition-all duration-200"
            } ${onPositionChange ? "cursor-grab group/txt" : ""}`}
          >
            <div className="relative">
              <h1 className="text-xs sm:text-base md:text-lg font-semibold opacity-90 leading-snug sm:leading-relaxed drop-shadow-sm select-none">
                {data.text}
              </h1>
              {onPositionChange && draggingItem !== "text" && (
                <div className="absolute -top-2 -right-2 sm:-top-3 sm:-right-3 opacity-0 group-hover/txt:opacity-100 transition-opacity bg-blue-600 text-white w-4 h-4 sm:w-6 sm:h-6 rounded-full flex items-center justify-center text-[8px] sm:text-[10px] shadow-md border border-white pointer-events-none">
                  <FontAwesomeIcon icon={faUpDownLeftRight} />
                </div>
              )}
            </div>
          </div>
        )}

        {/* BUTTON (DRAGGABLE) */}
        {data.button_text && (
          <div
            onMouseDown={(e) => handleStartDrag(e, "button")}
            onTouchStart={(e) => handleStartDrag(e, "button")}
            style={buttonStyle}
            className={`absolute pointer-events-auto w-max max-w-full ${
              draggingItem === "button" ? "z-30 cursor-grabbing scale-105 shadow-2xl transition-none" : "transition-all duration-200"
            } ${onPositionChange ? "cursor-grab group/btn" : ""}`}
          >
            <div className="relative">
              <a
                href={data.button_link || "#"}
                onClick={(e) => e.preventDefault()}
                className="inline-block px-2.5 py-1.5 sm:px-4 sm:py-3 rounded-md font-bold text-[10px] sm:text-xs md:text-sm uppercase tracking-wider sm:tracking-widest shadow-lg whitespace-nowrap select-none"
                style={{
                  backgroundColor: data.button_bg_color,
                  color: data.button_text_color,
                }}
              >
                {data.button_text}
              </a>

              {onPositionChange && draggingItem !== "button" && (
                <div className="absolute -top-2 -right-2 sm:-top-3 sm:-right-3 opacity-0 group-hover/btn:opacity-100 transition-opacity bg-blue-600 text-white w-4 h-4 sm:w-6 sm:h-6 rounded-full flex items-center justify-center text-[8px] sm:text-[10px] shadow-md border border-white pointer-events-none">
                  <FontAwesomeIcon icon={faUpDownLeftRight} />
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* FRAME DECORATION */}
      <div className="absolute inset-0 border-[4px] sm:border-[8px] md:border-[12px] border-white/10 pointer-events-none" />
    </div>
  );
};

export default BannerPreview;